(ns hypnos.backoffice.panel.patient
  "The patient page.

  Handles new and existing patients."
  (:require [hypnos.backoffice.core.auth :as auth]
            [hypnos.backoffice.core.patient :as core-patient]
            [hypnos.backoffice.subs]
            [hypnos.backoffice.tile.anatomy :as anatomy]
            [hypnos.backoffice.tile.communications :as communications]
            [hypnos.backoffice.tile.consults :as consults]
            [hypnos.backoffice.tile.demographics :as demographics]
            [hypnos.backoffice.tile.recommendations :as recommendations]
            [hypnos.graphql.queries :as queries]
            [re-frame.core :as rf]
            [reagent.core :as reagent]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Identities

(def ^{:private true} defaults
  {;; states
   :loading?                       false ; loading in progress?
   :patient-looked-up?             false ; did we lookup the patient
   :new-patient?                   true  ; a new patient?
   :new-consult?                   false ; create a new consult?
   :new-exchange?                  false ; new consult is an exchange?
   :edit-patient?                  false ; currently editing the patient demographics
   :one-time-message?              false ; sending a one-time message?
   :therapist-assisted-assessment? false ; does the therapist fill out the patient survey?
   ;; consult
   :patient                        {:patient/id                        {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/key                       {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/first-name                {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/last-name                 {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/gender                    {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/dob                       {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/email                     {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false
                                                                        :valid?   nil}
                                    :patient/phone-number              {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false
                                                                        :valid?   nil}
                                    :patient/communication-preferences {:value    #{:communication-channel/sms}
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/image                     {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    ;; current mask
                                    :patient/mask                      {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/sized-mask                {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/selector-type             {:value    :selector-type/therapist
                                                                        :dirty?   false
                                                                        :touched? false}
                                    :patient/selector                  {:value    nil
                                                                        :dirty?   false
                                                                        :touched? false}
                                    ;; the last consult
                                    :patient/last-consult              {:consult/id                    {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/user                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        ;; therapist submitted data
                                                                        :consult/service-type          {:value    :service-type/new-setup
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/manufacturer          {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/make                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/model                 {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/mask-was-comfortable? {:value    true
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/ahi                   {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/modality              {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/cpap                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/ipap                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/epap                  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        ;; patient submitted data
                                                                        :consult/weight                {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/take-dentures-out?    {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/sleep-positions       {:value    #{}
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/sleep-motion?         {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/mouth-breathing-level {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/eye-dryness?          {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/claustrophobia-level  {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/issues                {:value    #{}
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/chronic-issues?       {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/deviated-septum?      {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/pacemaker?            {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}
                                                                        :consult/status                {:value    nil
                                                                                                        :dirty?   false
                                                                                                        :touched? false}}
                                    :patient/consults                  []}
   :pixels                         {:anatomy/iris-diameter nil
                                    :anatomy/head-height   nil
                                    :anatomy/head-width    nil
                                    :anatomy/nose-width    nil
                                    :anatomy/nose-length   nil
                                    :anatomy/nose-to-clef  nil}
   :anatomy                        {:anatomy/id                      nil
                                    :anatomy/iris-diameter           12
                                    :anatomy/head-height             nil
                                    :anatomy/head-width              nil
                                    :anatomy/nose-width              nil
                                    :anatomy/nose-length             nil
                                    :anatomy/nose-to-clef            nil
                                    :anatomy/facial-hair?            nil
                                    :anatomy/eye-glass-indentations? nil}
   :recommendations                {:system []
                                    :note   nil
                                    :custom {:manufacturer  {:value    nil
                                                             :dirty?   false
                                                             :touched? false}
                                             :make          {:value    nil
                                                             :dirty?   false
                                                             :touched? false}
                                             :model         {:value    nil
                                                             :dirty?   false
                                                             :touched? false}
                                             :selector-type {:value    :selector-type/therapist
                                                             :dirty?   false
                                                             :touched? false}}}})

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- default-patient-timezone
  [state org]
  (when-not (get-in @state [:patient :patient/timezone :value])
    (swap! state
           assoc-in [:patient :patient/timezone :value]
           (:organization/timezone org))))

(defn- iris-actionable?
  "Is the consult actionable for IRIS (super) users?"
  [state]
  (let [patient        (:patient state)
        patient-image  (get-in patient [:patient/image :value])
        consult        (:patient/last-consult patient)
        consult-status (get-in consult [:consult/status :value])]
    (or (= :consult-state/in-progress consult-status)
        patient-image))
  #_(let [consult-status (:consult/status consult)
          patient        (:consult/patient consult)
          recomm-model   (:recommendation/model (first (:consult/recommendations consult)))]
      (or (= :consult-state/in-progress consult-status)
          (and (= :consult-state/pending consult-status)
               (:patient/image patient)
               (= :mask-size/fit-kit (:model/size recomm-model)))
          (and (= :consult-state/completed consult-status)
               (:patient/image patient)
               (= :mask-size/fit-kit (:model/size recomm-model))))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Panel

(defn panel
  "The patient panel"
  [{:keys [_patient-id]}]
  (let [patient (rf/subscribe [:backoffice/patient])
        state   (reagent/atom defaults)]
    (rf/dispatch-sync [:backoffice/reset-patient])
    (fn [{:keys [patient-id]}]
      (if patient-id
        (when-not @patient
          (rf/dispatch [:amplify/query
                        queries/get-patient-by-id
                        {:id patient-id}
                        :backoffice/set-patient
                        :backoffice/error]))
        (swap! state assoc :new-consult? true))
      (let [auth         (rf/subscribe [:backoffice/auth])
            catalog      (rf/subscribe [:backoffice/catalog])
            organization (rf/subscribe [:backoffice/organization])
            _            (default-patient-timezone state @organization)
            patient      (rf/subscribe [:backoffice/patient])
            loading?     (rf/subscribe [:backoffice/gql-request-in-progress?])
            _            (when-not @loading? (core-patient/merge-patient-into-state state @patient @catalog))]
        [:section.is-main-section
         [:div.section [demographics/tile state]
          ;; patient anatomy
          (when (and (auth/authorized? @auth :make-recommendation)
                     (iris-actionable? @state))
            [anatomy/tile state])
          ;; make recommendations
          (when (and (auth/authorized? @auth :make-recommendation)
                     (contains? #{:consult-state/in-progress :consult-state/pending}
                                (get-in @state [:patient :patient/last-consult :consult/status :value])))
            [recommendations/tile state])
          ;; prior consults
          [consults/prior-consults-tile state]
          ;; communications
          [communications/tile]]]))))
